// client/src/components/vatCredit/vatCreditUtils.js
import { getCurrencySymbol } from '../utils/formatUtils';

export const VAT_CREDIT_TYPES = ['tr_model', 'annual_declaration'];
export const VAT_CREDIT_COVERAGES = ['visa', 'guarantee', 'not_required'];
// Il 4° trimestre non si chiede con il Modello TR (rientra nella dichiarazione annuale)
export const VAT_CREDIT_QUARTERS = [1, 2, 3];
// Le ACT, più usate, in cima ai menu a tendina
export const VAT_CREDIT_DAY_COUNT_BASES = ['act_360', 'act_365', '30_360', '30_360_us', '30e_360'];

// 'YYYY-MM-DD' di una data nel fuso di Roma (per i `min` degli input date)
export const toRomeDateInputValue = (date) =>
  new Date(date).toLocaleDateString('en-CA', { timeZone: 'Europe/Rome' });

export const formatDayCountBasis = (bid, t) =>
  bid?.dayCountBasis ? t(`vatCredit.dayCountBases.${bid.dayCountBasis}`) : null;

export const formatOptionalDate = (date, t) =>
  date ? new Date(date).toLocaleDateString() : t('bids.notAvailable');

// --- Costo dell'offerta per l'azienda ---
// Stessa formula di server/utils/vatCreditCost.js:
//   interessi = importo × tasso × giorni (erogazione → maturity ufficiale) / base di calcolo
//   costo totale = interessi + importo × commissione + spese

const MS_PER_DAY = 24 * 60 * 60 * 1000;
export const DAY_COUNT_DENOMINATORS = { act_360: 360, act_365: 365, '30_360': 360, '30_360_us': 360, '30e_360': 360 };

const roundToCents = (v) => Math.round(v * 100) / 100;

// Importo con simbolo valuta e sempre 2 decimali (es. "€ 4,736.99")
export const formatCostAmount = (amount, currency) =>
  `${getCurrencySymbol(currency)} ${(amount || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

const THIRTY_360_BASES = ['30_360', '30_360_us', '30e_360'];

const isLastDayOfFebruary = (date) =>
  date.getUTCMonth() === 1 && new Date(date.getTime() + MS_PER_DAY).getUTCMonth() === 2;

// Giorni con le convenzioni 30/360 (mesi da 30 giorni); cambia solo l'aggiustamento di D1/D2:
//   30/360 (Bond Basis): D1 31 → 30; D2 31 → 30 solo se D1 è 30
//   30/360 US: come Bond Basis, ma l'ultimo giorno di febbraio conta come 30
//   30E/360 (Eurobond): D1 e D2 31 → 30 sempre
const count30360Days = (start, end, dayCountBasis) => {
  let d1 = start.getUTCDate();
  let d2 = end.getUTCDate();

  if (dayCountBasis === '30e_360') {
    d1 = Math.min(d1, 30);
    d2 = Math.min(d2, 30);
  } else {
    if (dayCountBasis === '30_360_us' && isLastDayOfFebruary(start)) {
      if (isLastDayOfFebruary(end)) d2 = 30;
      d1 = 30;
    }
    d1 = Math.min(d1, 30);
    if (d1 === 30) d2 = Math.min(d2, 30);
  }

  return 360 * (end.getUTCFullYear() - start.getUTCFullYear()) +
    30 * (end.getUTCMonth() - start.getUTCMonth()) +
    (d2 - d1);
};

// Giorni tra due date secondo la base di calcolo (giorni effettivi per le ACT)
export const countDays =(startDate, endDate, dayCountBasis) => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (THIRTY_360_BASES.includes(dayCountBasis)) {
    return count30360Days(start, end, dayCountBasis);
  }

  return Math.round((end - start) / MS_PER_DAY);
};

/**
 * Calcola interessi, commissione, spese e costo totale di un'allocazione su un'offerta.
 * `bid.effectiveRate` arriva dal server (tasso fisso, oppure ultimo Euribor + spread).
 * Restituisce null se mancano i dati (offerte legacy).
 */
export const computeBidCost = (bid, amount, executionDate) => {
  const denominator = DAY_COUNT_DENOMINATORS[bid?.dayCountBasis];
  if (!denominator || !bid.officialMaturityDate || !executionDate ||
      bid.effectiveRate === null || bid.effectiveRate === undefined) {
    return null;
  }

  // Interessi e commissione arrotondati al centesimo: il totale è la somma dei valori mostrati
  const days = countDays(executionDate, bid.officialMaturityDate, bid.dayCountBasis);
  const interestAmount = roundToCents(amount * (bid.effectiveRate / 100) * (days / denominator));
  const commissionAmount = roundToCents(amount * ((bid.commission || 0) / 100));
  const fees = bid.fees || 0;

  return {
    effectiveRate: bid.effectiveRate,
    days,
    interestAmount,
    commissionAmount,
    fees,
    totalCost: roundToCents(interestAmount + commissionAmount + fees)
  };
};

// Tipo di credito con periodo: "Modello TR · 2026 · 2° trimestre" / "Dichiarazione IVA annuale · 2025".
// Le richieste legacy non hanno i dati del credito: restituisce null.
export const formatCreditTypeAndPeriod = (request, t) => {
  if (!request?.creditType) return null;
  const parts = [t(`vatCredit.creditTypes.${request.creditType}`), request.creditYear];
  if (request.creditQuarter) {
    parts.push(t('vatCredit.quarterOption', { n: request.creditQuarter }));
  }
  return parts.filter(Boolean).join(' · ');
};

// Termine Maturity indicativo; le richieste legacy hanno solo i giorni convenzionati
export const formatMaturity = (request, t) => {
  if (request?.maturityDate) return new Date(request.maturityDate).toLocaleDateString();
  if (request?.indicativeDays !== undefined && request?.indicativeDays !== null) {
    return `${request.indicativeDays} ${t('common.days')}`;
  }
  return t('bids.notAvailable');
};

export const formatCoverage = (request, t) =>
  request?.coverage ? t(`vatCredit.coverages.${request.coverage}`) : null;

// Tipi di tasso da quotare: "Fisso / Variabile"; null per le richieste legacy
export const formatRequestedRateTypes = (request, t) =>
  request?.requestedRateTypes?.length
    ? request.requestedRateTypes.map(rt => t(`vatCredit.rateTypes.${rt}`)).join(' / ')
    : null;
